import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import {
    AnnualReportProvider,
    ISectionData,
    MarkerClasses,
    PrintUsingPdfTronDto,
    SectionIds,
} from '@modeso/aumico-lib-annualreport-fe';
import { ComplementaryReportFilesActions } from '@modeso/aumico-lib-annualreport-fe/src/lib/actions/complementary-report-files.actions';
import { ComplementaryReportFilesProvider } from '@modeso/aumico-lib-annualreport-fe/src/lib/provider/complementary-report-files.provider';
import { EMPTY_ARRAY_LENGTH } from '@modeso/aumico-lib-core-fe';
import { FilePreviewStatus } from '@modeso/aumico-lib-cpmm-fe/enums/FilePreviewStatus.enum';
import { IStyleOptions } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import { filter, map, Subscription, take } from 'rxjs';
import { ReportSectionsHelper } from '../../helpers/report-sections/report-sections-helper';
@UntilDestroy()
@Component({
    selector: 'aumico-annualReport-complementary-report-german-print-preview-page',
    templateUrl: 'comp-report-german-print-preview.page.html',
    styleUrls: ['comp-report-german-print-preview.page.scss'],
})
export class ComplementaryReportsGermanPrintPreviewPage implements OnInit {
    @Input() stylePreferenceOptions: IStyleOptions | undefined;

    @Input() complementaryReportSectionId: string | undefined;

    public markerClasses = MarkerClasses;

    complementaryReport: ISectionData | undefined;

    public reportIndex: number;
    public complementaryReportPreviews: { [fileId: string]: { previewUrl: string; previewStatus: FilePreviewStatus } } =
        {};

    // Track polling subscriptions and timeouts per file
    private pollSubs = new Map<string, Subscription>();
    private pollTimeouts = new Map<string, any>();
    private pollingDelays = [500, 500, 1000, 1500, 2500, 2500, 2500, 2500, 2500, 2500];
    isPreviewModalOpen: boolean;

    constructor(
        private readonly annualReportProvider: AnnualReportProvider,
        private readonly actions$: ActionsSubject,
        private cdr: ChangeDetectorRef,
        private readonly complementaryReportFilesProvider: ComplementaryReportFilesProvider,
        private readonly localizedTexts: I18NextService,
    ) { }

    ngOnInit(): void {
        this.annualReportProvider
            .getPrintView$()
            .pipe(
                filter((printViewData: PrintUsingPdfTronDto) => printViewData != null),
                map((printViewData: PrintUsingPdfTronDto) => printViewData.data),
                untilDestroyed(this),
            )
            .subscribe((data) => {
                if (this.complementaryReportSectionId.includes(SectionIds.COMPLEMENTARY_REPORT)) {
                    this.complementaryReportSectionId = ReportSectionsHelper.mapSectionIdsToPdfSectionIds(
                        this.complementaryReportSectionId as SectionIds,
                    );
                }

                if (data.crs.length > EMPTY_ARRAY_LENGTH) {
                    // if crs have no print locations
                    this.complementaryReport = data.crs.find(
                        (report) => report.sectionId === this.complementaryReportSectionId,
                    );
                } else {
                    if (data.crsA01.length > EMPTY_ARRAY_LENGTH) {
                        this.complementaryReport = data.crsA01.find(
                            (report) => report.sectionId === this.complementaryReportSectionId,
                        );
                    }
                    if (this.complementaryReport == null && data.crsA02.length > EMPTY_ARRAY_LENGTH) {
                        this.complementaryReport = data.crsA02.find(
                            (report) => report.sectionId === this.complementaryReportSectionId,
                        );
                    }
                    if (this.complementaryReport == null && data.crsA03.length > EMPTY_ARRAY_LENGTH) {
                        this.complementaryReport = data.crsA03.find(
                            (report) => report.sectionId === this.complementaryReportSectionId,
                        );
                    }
                }

                this.reportIndex = this.extractNumber(this.complementaryReportSectionId);
            });
        // Store preview status in complementaryReportPreviews
        this.actions$
            .pipe(ofType(ComplementaryReportFilesActions.checkPreviewStatusSuccess), untilDestroyed(this))
            .subscribe(({ fileId, status }) => {
                if (!fileId) return;
                if (!this.complementaryReportPreviews[fileId]) {
                    // initialize entry to avoid runtime errors
                    this.complementaryReportPreviews[fileId] = { previewUrl: '', previewStatus: status };
                } else {
                    this.complementaryReportPreviews[fileId].previewStatus = status;
                }

                this.cdr.detectChanges();
            });

        // Store preview URL in complementaryReportPreviews when it's loaded
        this.actions$
            .pipe(ofType(ComplementaryReportFilesActions.loadPreviewUrlSuccess), untilDestroyed(this))
            .subscribe(({ fileId, url }) => {
                if (!fileId) return;
                if (!this.complementaryReportPreviews[fileId]) {
                    this.complementaryReportPreviews[fileId] = {
                        previewUrl: url,
                        previewStatus: FilePreviewStatus.GENERATED,
                    };
                } else {
                    this.complementaryReportPreviews[fileId].previewUrl = url;
                    this.complementaryReportPreviews[fileId].previewStatus = FilePreviewStatus.GENERATED;
                }
                this.cdr.detectChanges();
            });
    }

    private extractNumber(id: string): number | null {
        const match = id.match(/\d+$/);
        return match ? parseInt(match[0], 10) : null;
    }

    public onPreviewRequested(fileId: string): void {
        // Handle the preview request here
        this.onStartPolling(fileId);
        this.isPreviewModalOpen = true;
    }

    public onClosePreviewModal(): void {
        this.isPreviewModalOpen = false;
    }

    onStartPolling(fileId: string): void {
        const delays = [...this.pollingDelays];
        const tick = () => {
            if (!delays.length) {
                this.onStopPolling(fileId);
                return;
            }
            const delay = delays.shift() as number;
            const timeout = setTimeout(() => {
                const sub = this.actions$
                    .pipe(
                        ofType<ReturnType<typeof ComplementaryReportFilesActions.checkPreviewStatusSuccess>>(
                            ComplementaryReportFilesActions.checkPreviewStatusSuccess,
                        ),
                        filter((a) => a.fileId === fileId),
                        take(1),
                        untilDestroyed(this),
                    )
                    .subscribe(({ status }) => {
                        if (status === FilePreviewStatus.GENERATED) {
                            this.complementaryReportFilesProvider.dispatchLoadPreviewUrl(fileId);
                            this.onStopPolling(fileId);
                        } else if (status === FilePreviewStatus.PENDING || status === FilePreviewStatus.SCANNING) {
                            tick();
                        } else if (status === FilePreviewStatus.NOT_STARTED) {
                            this.complementaryReportFilesProvider.dispatchRequestPreviewGeneration(fileId);
                            tick();
                        } else {
                            this.onStopPolling(fileId);
                        }
                    });
                this.pollSubs.set(fileId, sub);
                this.complementaryReportFilesProvider.dispatchCheckPreviewStatus(fileId);
            }, delay);
            this.pollTimeouts.set(fileId, timeout);
        };
        tick();
    }

    onStopPolling(fileId: string): void {
        const sub = this.pollSubs.get(fileId);
        if (sub) {
            sub.unsubscribe();
            this.pollSubs.delete(fileId);
        }
        const timeout = this.pollTimeouts.get(fileId);
        if (timeout) {
            clearTimeout(timeout);
            this.pollTimeouts.delete(fileId);
        }
    }

    public getPreviewStatusText(status: string): string {
        switch (status) {
            case FilePreviewStatus.NOT_STARTED:
                return this.localizedTexts.t('aumico_app_cpmm_documents_wait_for_anti_virus') as string;
            case FilePreviewStatus.PENDING:
                return this.localizedTexts.t('aumico_app_cpmm_documents_preview_loading') as string;
            case FilePreviewStatus.GENERATED:
                return this.localizedTexts.t('aumico_app_cpmm_documents_preview_loading') as string;
            case FilePreviewStatus.FAILED:
                return this.localizedTexts.t('aumico_app_cpmm_documents_preview_failed') as string;
            case FilePreviewStatus.NOT_SUPPORTED:
                return this.localizedTexts.t('aumico_app_cpmm_documents_preview_not_supported') as string;
            case FilePreviewStatus.SCANNING:
                return this.localizedTexts.t('aumico_app_cpmm_documents_wait_for_anti_virus') as string;
            default:
                return status;
        }
    }

    public decodeFileName(fileName: string | undefined): string | undefined {
        const possibleMojibakeRegex = /[\u00C2-\u00F4]/;

        if (fileName == null) {
            return fileName;
        } else if (!possibleMojibakeRegex.test(fileName)) {
            return fileName;
        }
        try {
            // Fix mojibake: UTF-8 bytes were interpreted as Latin-1/ISO-8859-1
            // Re-encode as Latin-1 bytes, then decode as UTF-8
            const bytes = new Uint8Array([...fileName].map((c) => c.charCodeAt(0)));
            return new TextDecoder('utf-8').decode(bytes);
        } catch {
            // If decoding fails, return original
            return fileName;
        }
    }
}
